'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ExternalLink, Home, Key, Layers, MapPin, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  GeoBoundsResponse,
  MapCity,
  MapDistrict,
  MapProperty,
  MapRegion,
  TxTypeFilter,
} from './types';

const BROWSER_API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';

const MOROCCO_CENTER: [number, number] = [31.7917, -7.0926];
const MOROCCO_ZOOM = 6;

const LeafletMap = dynamic(() => import('./LeafletMap'), {
  ssr: false,
  loading: () => (
    <div className="w-full h-full bg-surface-cream rounded-b-2xl flex flex-col items-center justify-center gap-3">
      <span className="w-7 h-7 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      <span className="text-sm text-ink-muted font-medium">Chargement de la carte…</span>
    </div>
  ),
});

// ──────────────────────────────────────────────────────────────────────
interface SelectOption {
  id: string;
  name: string;
}

interface GeoSelectProps {
  label: string;
  value: SelectOption | null;
  options: SelectOption[];
  placeholder: string;
  disabled?: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (opt: SelectOption | null) => void;
  alignRight?: boolean;
}

function GeoSelect({
  label,
  value,
  options,
  placeholder,
  disabled,
  isOpen,
  onToggle,
  onSelect,
  alignRight,
}: GeoSelectProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onToggle();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onToggle]);

  return (
    <div ref={ref} className="relative flex-1 min-w-0">
      <button
        type="button"
        disabled={disabled}
        onClick={onToggle}
        className={cn(
          'w-full flex items-center gap-2.5 px-5 py-4 text-left transition-colors',
          isOpen ? 'bg-surface-cream/60' : 'hover:bg-surface-cream/30',
          disabled && 'opacity-40 pointer-events-none',
        )}
      >
        <MapPin
          className={cn(
            'h-4 w-4 shrink-0 transition-colors',
            value ? 'text-accent' : 'text-ink-muted',
          )}
        />
        <div className="flex-1 min-w-0">
          <p className="eyebrow text-[10px] leading-none mb-1">{label}</p>
          <p
            className={cn(
              'text-sm truncate',
              value ? 'text-ink font-medium' : 'text-ink-muted',
            )}
          >
            {value?.name ?? placeholder}
          </p>
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-ink-muted shrink-0 transition-transform duration-250',
            isOpen && 'rotate-180 text-accent',
          )}
        />
      </button>

      {isOpen && (
        <div
          className={cn(
            'absolute top-full mt-2 w-64 bg-surface rounded-2xl shadow-card-hover border border-border-subtle z-[60] overflow-hidden',
            alignRight ? 'right-0' : 'left-0',
          )}
        >
          <div className="max-h-60 overflow-y-auto py-1.5">
            <button
              type="button"
              onMouseDown={() => onSelect(null)}
              className={cn(
                'w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2',
                !value
                  ? 'text-accent font-semibold bg-accent/[0.08]'
                  : 'text-ink-secondary hover:bg-surface-cream/50',
              )}
            >
              {!value && <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />}
              {placeholder}
            </button>

            {options.length === 0 && (
              <p className="px-4 py-3 text-xs text-ink-muted text-center">Aucun résultat</p>
            )}

            {options.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onMouseDown={() => onSelect(opt)}
                className={cn(
                  'w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2',
                  value?.id === opt.id
                    ? 'text-accent font-semibold bg-accent/[0.08]'
                    : 'text-ink-secondary hover:bg-surface-cream/50',
                )}
              >
                {value?.id === opt.id && <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />}
                {opt.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
export function PropertyMapSearch() {
  const [regions, setRegions] = useState<MapRegion[]>([]);
  const [cities, setCities] = useState<MapCity[]>([]);
  const [districts, setDistricts] = useState<MapDistrict[]>([]);

  const [selRegion, setSelRegion] = useState<MapRegion | null>(null);
  const [selCity, setSelCity] = useState<MapCity | null>(null);
  const [selDistrict, setSelDistrict] = useState<MapDistrict | null>(null);

  const [txFilter, setTxFilter] = useState<TxTypeFilter>('all');

  const [mapCenter, setMapCenter] = useState<[number, number]>(MOROCCO_CENTER);
  const [mapZoom, setMapZoom] = useState(MOROCCO_ZOOM);
  const [mapBounds, setMapBounds] = useState<[[number, number], [number, number]] | undefined>(undefined);

  const [allProperties, setAllProperties] = useState<MapProperty[]>([]);
  const [loadingProps, setLoadingProps] = useState(true);

  const [openDropdown, setOpenDropdown] = useState<'region' | 'city' | 'district' | null>(null);
  const toggle = (name: 'region' | 'city' | 'district') =>
    setOpenDropdown((prev) => (prev === name ? null : name));

  const displayedProperties = useMemo(
    () =>
      txFilter === 'all'
        ? allProperties
        : allProperties.filter((p) => p.transactionType === txFilter),
    [allProperties, txFilter],
  );

  useEffect(() => {
    fetch(`${BROWSER_API}/api/regions`)
      .then((r) => r.json())
      .then((d) => setRegions(Array.isArray(d) ? d : []))
      .catch(() => { });
  }, []);

  useEffect(() => {
    if (!selRegion) {
      setCities([]);
      return;
    }
    fetch(`${BROWSER_API}/api/cities?regionId=${selRegion.id}`)
      .then((r) => r.json())
      .then((d) => setCities(Array.isArray(d) ? d : []))
      .catch(() => { });
  }, [selRegion]);

  useEffect(() => {
    if (!selCity) {
      setDistricts([]);
      return;
    }
    fetch(`${BROWSER_API}/api/districts?cityId=${selCity.id}`)
      .then((r) => r.json())
      .then((d) => setDistricts(Array.isArray(d) ? d : []))
      .catch(() => { });
  }, [selCity]);

  const fetchBounds = useCallback(
    async (type: 'region' | 'city' | 'district', id: string) => {
      try {
        const r = await fetch(`${BROWSER_API}/api/geo/bounds/${type}/${id}`);
        if (!r.ok) return;
        const data: GeoBoundsResponse = await r.json();
        setMapBounds(data.bounds);
        setMapCenter(data.center);
        setMapZoom(type === 'region' ? 9 : type === 'city' ? 12 : 14);
      } catch { }
    },
    [],
  );

  useEffect(() => {
    if (selDistrict) fetchBounds('district', selDistrict.id);
    else if (selCity) fetchBounds('city', selCity.id);
    else if (selRegion) fetchBounds('region', selRegion.id);
    else {
      setMapBounds(undefined);
      setMapCenter(MOROCCO_CENTER);
      setMapZoom(MOROCCO_ZOOM);
    }
  }, [selRegion, selCity, selDistrict, fetchBounds]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (selRegion) params.set('regionSlug', selRegion.slug);
    if (selCity) params.set('citySlug', selCity.slug);
    const qs = params.toString();
    setLoadingProps(true);
    fetch(`${BROWSER_API}/api/properties/map${qs ? `?${qs}` : ''}`)
      .then((r) => r.json())
      .then((d) => setAllProperties(Array.isArray(d) ? d : []))
      .catch(() => setAllProperties([]))
      .finally(() => setLoadingProps(false));
  }, [selRegion, selCity]);

  const clearAll = () => {
    setSelRegion(null);
    setSelCity(null);
    setSelDistrict(null);
    setCities([]);
    setDistricts([]);
    setTxFilter('all');
    setOpenDropdown(null);
  };

  const geoCount = displayedProperties.length;
  const hasFilters = !!(selRegion || selCity || selDistrict) || txFilter !== 'all';
  const listBase = txFilter === 'rent' ? 'louer' : 'acheter';
  const listQuery = selCity
    ? `?citySlug=${selCity.slug}`
    : selRegion
      ? `?regionSlug=${selRegion.slug}`
      : '';

  const txTabs: { key: TxTypeFilter; label: string; Icon: typeof Layers }[] = [
    { key: 'all', label: 'Tous', Icon: Layers },
    { key: 'sale', label: 'Vente', Icon: Home },
    { key: 'rent', label: 'Location', Icon: Key },
  ];

  return (
    <div className="rounded-2xl shadow-card-hover overflow-visible bg-surface border border-border-subtle">
      <div className="relative z-10 rounded-t-2xl overflow-visible">
        {/* Row 1: Zone selects + count */}
        <div className="flex flex-col sm:flex-row sm:items-stretch sm:divide-x divide-border-subtle border-b border-border-subtle rounded-t-2xl">

          {/* Zone label — hidden on mobile */}
          <div className="hidden sm:flex items-center gap-2 px-4 py-3.5 shrink-0">
            <Layers className="h-4 w-4 text-accent shrink-0" />
            <span className="eyebrow text-[10px] whitespace-nowrap">Zone</span>
          </div>

          {/* 3 selects — stack on mobile, row on sm+ */}
          <div className="flex flex-col divide-y sm:flex-row sm:flex-1 sm:divide-y-0 sm:divide-x divide-border-subtle border-b sm:border-0 border-border-subtle">
            <GeoSelect
              label="Région"
              value={selRegion}
              options={regions}
              placeholder="Toutes les régions"
              isOpen={openDropdown === 'region'}
              onToggle={() => toggle('region')}
              onSelect={(opt) => {
                setSelRegion(opt as MapRegion | null);
                setSelCity(null);
                setSelDistrict(null);
                setOpenDropdown(null);
              }}
            />

            <GeoSelect
              label="Ville"
              value={selCity}
              options={cities}
              placeholder="Toutes les villes"
              disabled={!selRegion}
              isOpen={openDropdown === 'city'}
              onToggle={() => toggle('city')}
              onSelect={(opt) => {
                setSelCity(opt as MapCity | null);
                setSelDistrict(null);
                setOpenDropdown(null);
              }}
            />

            <GeoSelect
              label="Quartier"
              value={selDistrict}
              options={districts}
              placeholder="Tous les quartiers"
              disabled={!selCity}
              isOpen={openDropdown === 'district'}
              onToggle={() => toggle('district')}
              onSelect={(opt) => {
                setSelDistrict(opt as MapDistrict | null);
                setOpenDropdown(null);
              }}
              alignRight
            />
          </div>

          {/* Count + clear */}
          <div className="flex items-center justify-between sm:justify-end gap-3 px-4 py-3 sm:py-4 sm:shrink-0">
            <div className="flex flex-col items-end gap-0.5">
              <p className="eyebrow text-[10px] leading-none">Annonces</p>
              {loadingProps ? (
                <span className="inline-block w-12 h-3 bg-surface-cream rounded animate-pulse" />
              ) : (
                <p
                  className={cn(
                    'text-sm font-semibold tabular-nums leading-none',
                    geoCount > 0 ? 'text-accent' : 'text-ink-muted',
                  )}
                >
                  {geoCount} <span className="text-xs font-normal text-ink-muted">sur carte</span>
                </p>
              )}
            </div>
            {hasFilters && (
              <button
                type="button"
                onClick={clearAll}
                className="p-1.5 rounded-full hover:bg-surface-cream text-ink-muted hover:text-ink transition-colors"
                title="Réinitialiser les filtres"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Row 2: Transaction tabs + link */}
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-border-subtle">
          <span className="eyebrow text-[10px] mr-1 shrink-0">Type</span>
          {txTabs.map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTxFilter(key)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition-all duration-250 border',
                txFilter === key
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-surface text-ink-secondary border-border-subtle hover:border-accent/40 hover:text-accent',
              )}
            >
              <Icon className="h-3 w-3 shrink-0" />
              {label}
            </button>
          ))}
          {hasFilters && (
            <a
              href={`/${listBase}${listQuery}`}
              className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-accent link-underline shrink-0"
            >
              <ExternalLink className="h-3 w-3" />
              Voir toutes les annonces
            </a>
          )}
        </div>
      </div>

      <div className="relative z-0 h-[520px] overflow-hidden">
        <LeafletMap
          center={mapCenter}
          zoom={mapZoom}
          bounds={mapBounds}
          properties={displayedProperties}
          isLoading={loadingProps}
        />
      </div>

      <div className="px-5 py-2.5 border-t border-border-subtle rounded-b-2xl bg-surface-cream/30">
        <p className="text-[11px] text-ink-muted text-center tracking-wide">
          Survolez un marqueur pour un aperçu · Cliquez pour les détails complets
        </p>
      </div>
    </div>
  );
}
